import React, {memo} from 'react';
import Editor from "@monaco-editor/react";
import DiffViewer from "@src/ui/DiffViewer";
import ResponseRenderer from "@src/ui/ResponseRenderer";
import GenUIRenderer from "@src/ui/GenUIRenderer";

export const tryParseToJSON = (value: any) => {
    let parsedData: any;
    if(value == null)
        parsedData = {}
    else if (typeof value === 'string')
        try {
            parsedData = JSON.parse(value);
        } catch {
            parsedData = value;
        }
    else
        parsedData = value;
    return parsedData;
}

export interface PackDataExpandedRowProps {
    initialValue?: any;
    compareValue?: any;
    isCompareMode?: boolean;
    dataPipeline?: any;
    dataComparePipeline?: any;
    isEditing: boolean;
    editedJson: string;
    onEditedJsonChange: (value: string) => void;
    viewMode: 'enhanced' | 'raw';
    genUIData?: any;
    /* The Edit button that turns isEditing on lives in the panel above and is
       gone in read-only mode, so this never fires. Belt as well as braces: a
       writable editor is the one thing here that could change the Pack. */
    readOnly?: boolean;
}

/**
 * Pack stage data for non-UE pipelines. Fully controlled: the view switch and
 * Edit/Submit/Cancel buttons live in the PipelinePanel above.
 */
const PackDataExpandedRow = memo(({
    initialValue = {},
    compareValue = null,
    isCompareMode = false,
    dataPipeline,
    dataComparePipeline,
    isEditing,
    editedJson,
    onEditedJsonChange,
    viewMode,
    genUIData,
    readOnly
}: PackDataExpandedRowProps) => {
    const parsedData = tryParseToJSON(initialValue)

    let parsedCompareData: any = null;
    if(isCompareMode && compareValue != null)
        parsedCompareData = tryParseToJSON(compareValue);

    // Check if initialValue is empty
    const isEmptyData = !initialValue ||
        (typeof parsedData === 'object' &&
         !Array.isArray(parsedData) &&
         Object.keys(parsedData).length === 0);

    if (isCompareMode) {
        return (
            <DiffViewer
                oldValue={typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData || {}, null, 2)}
                newValue={typeof parsedCompareData === 'string' ? parsedCompareData : JSON.stringify(parsedCompareData || {}, null, 2)}
                leftTitle={`Pipeline${dataPipeline?.version ? ` ${dataPipeline.version}` : ''}`}
                rightTitle={`Pipeline${dataComparePipeline?.version ? ` ${dataComparePipeline.version}` : ''}`}
                compareMethod="lines"
            />
        );
    }

    if (isEditing && !readOnly) {
        return (
            <Editor
                height="300px"
                defaultLanguage={typeof parsedData === 'string' ? 'markdown' : 'json'}
                value={editedJson}
                onChange={(value) => onEditedJsonChange(value || '')}
                options={{ minimap: { enabled: false } }}
            />
        );
    }

    if (genUIData && viewMode === 'enhanced') {
        return <GenUIRenderer components={genUIData} />;
    }

    if (isEmptyData && !genUIData) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px', color: '#999' }}>
                No data found
            </div>
        );
    }

    return (
        <div style={{ width: '100%', overflow: 'hidden' }}>
            <ResponseRenderer response={initialValue} />
        </div>
    );
});

PackDataExpandedRow.displayName = 'PackDataExpandedRow';

export default PackDataExpandedRow;
